/**
 * 🔋 The house power meter (09-26, Nina: "a battery by the clock with hours
 * left", mockup https://claude.ai/artifact/DxmzXZxsGyiB5pYMgew3tU option A +
 * hours; then "in the place of the money bag at the top right", so it is the
 * masthead's last vitals chip, components/ChatGame/PowerMeter.js). Pure: the
 * status payload's top-level `ai_power` block (backend
 * src/services/AiPower.js, only present while HOUSE_ASLEEP_ENABLED is on) →
 * everything the meter draws. Absent block → null → nothing renders, so this
 * ships dark by construction and an old backend is safe.
 *
 * Copy rules (Nina + the asleep handoff): short, no em dashes, NEVER the word
 * "credits" (in the house that is the per-round speech credit), no promised
 * wake time, and a support link NEVER without its note (Nina 09-26: say the
 * money only runs the house and pays nobody; the note comes from the backend
 * so every surface shows the same words).
 *
 * 🔋 09-29 the 5 AM / 5 PM recharge (SPACES_REDESIGN_BRAINSTORM_2026_09_29.md
 * §3.2-§3.4, mockup "Keep the Lights On" §1-§3, "My pick"): when the backend's
 * AI_POWER_WINDOW_ENABLED is on, the block also carries next_recharge_at,
 * window_left_frac, asleep_reason and limited_by ('window' = the recharge
 * refills what runs out first). Then the note gets a countdown row in the
 * VIEWER's own clock ("Recharges at 5:00 PM · in 3h 12m", "(5 PM Phoenix)"
 * when their clock differs), the battery fills by the window, the body says
 * how the shared battery works, and the chip names the recharge only when it
 * is low (and the recharge lands first) or asleep. The ONE exception to "no
 * promised wake time": a sleep WE scheduled (asleep_reason 'window') says
 * "Back at 5 PM". A real $0 ('balance', or no reason) keeps today's words and
 * names no time. No window fields = everything exactly as before.
 *
 * 💛 09-29 Nina's picks (spec §9-bis): the link reads "Help keep the lights on
 * in {space name}" (lightsLabel), and when the backend's
 * AI_POWER_FUNDING_ENABLED is on (and a support link exists) the block carries
 * `funding` { month, frac, full, zero }: the note shows "This month, visitors
 * covered N% of the AI bill" over a thin bar, above the link, and "100% crowd
 * funded this month" only when the month's tips really cover its bill. No
 * `funding` = no meter, exactly as before.
 */

// Every ground it sits on is dark (the masthead's glass chip over the banner
// art, the note's brown card), so the house hues are lifted to stay legible
// as a 1.5px outline and small text there.
export const POWER_COLORS = {
  ok: '#9fd4ae',       // the house sage, lifted
  low: '#f0c96a',      // the till bar's gold
  critical: '#ff8f7d', // the house error red, lifted
  asleep: '#c2caf4',   // the night card's moon blue, lifted
};

const LEVELS = ['ok', 'low', 'critical', 'asleep'];
const DAY_HOURS = 24;
const PHOENIX = 'America/Phoenix'; // the recharge's own clock (MST all year: 5 AM = 12:00Z, 5 PM = 00:00Z)

// 🔋 The note's body while the recharge window is on (spec §3.4 A, the honest one).
export const RECHARGE_BODY = 'Every space here runs on one shared AI battery. It recharges at 5 AM and 5 PM Phoenix time. Tips help it last.';

/** '24h+' · '~9h' · '1h 20m' · '45m' · '' (unknown). */
export function hoursWords(h) {
  if (typeof h !== 'number' || !Number.isFinite(h) || h < 0) return '';
  if (h >= DAY_HOURS) return '24h+';
  if (h >= 2) return `~${Math.round(h)}h`;
  const mins = Math.max(1, Math.round(h * 60));
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

/** The same amount, spoken ("about 9 hours", "about 1 hour 20 minutes"). */
export function hoursSpoken(h) {
  if (typeof h !== 'number' || !Number.isFinite(h) || h < 0) return '';
  if (h >= 2) return `about ${Math.round(h)} hours`;
  const mins = Math.max(1, Math.round(h * 60));
  if (mins < 60) return `about ${mins} minutes`;
  const rest = mins % 60;
  return `about 1 hour${rest ? ` ${rest} minutes` : ''}`;
}

/** How full the drawn battery is (0..1). */
export function meterFill(level, h) {
  if (level === 'asleep') return 0.02;
  if (typeof h === 'number' && Number.isFinite(h)) return Math.max(0.06, Math.min(1, h / DAY_HOURS));
  return level === 'critical' ? 0.08 : level === 'low' ? 0.3 : 0.9;
}

/** "9:12pm" in the house's own zone (the viewer's outside the house); '' on bad input. */
export function clockIn(iso, tz) {
  const t = Date.parse(iso || '');
  if (!Number.isFinite(t)) return '';
  try {
    const s = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', ...(tz ? { timeZone: tz } : {}) })
      .format(new Date(t));
    return s.replace(/\s+/g, '').toLowerCase();
  } catch (e) {
    return '';
  }
}

/**
 * One instant on a clock: { full: '5:00 PM', short: '5 PM' } ('5:30 AM' keeps
 * its minutes). `tz` null = the viewer's own zone. Null on bad input.
 */
export function clockParts(ms, tz = null) {
  if (typeof ms !== 'number' || !Number.isFinite(ms)) return null;
  try {
    const parts = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, ...(tz ? { timeZone: tz } : {}) })
      .formatToParts(new Date(ms));
    const get = (type) => ((parts.find((x) => x.type === type) || {}).value || '');
    const hour = get('hour');
    const minute = get('minute');
    const period = get('dayPeriod').toUpperCase();
    if (!hour || !minute) return null;
    const tail = period ? ` ${period}` : '';
    return { full: `${hour}:${minute}${tail}`, short: `${hour}${minute === '00' ? '' : `:${minute}`}${tail}` };
  } catch (e) {
    return null;
  }
}

/** Time until `ms`, rounded UP to the minute: '3h 12m' · '3h' · '45m'; '' once it is due. */
export function untilWords(ms, now = Date.now()) {
  const left = ms - now;
  if (!(left > 0)) return '';
  const mins = Math.ceil(left / 60000);
  if (mins < 60) return `${mins}m`;
  const rest = mins % 60;
  return `${Math.floor(mins / 60)}h${rest ? ` ${rest}m` : ''}`;
}

/**
 * The status block's next_recharge_at → the countdown row, in the viewer's
 * clock: { at, clock: '5:00 PM', short: '5 PM', line: 'Recharges at 5:00 PM ·
 * in 3h 12m' (or 'Recharging now' once due), phoenix: '(5 PM Phoenix)' | null
 * (null when the viewer's clock reads the same) } | null (absent or bad).
 */
export function rechargeView(iso, { viewerTz = null, now = Date.now() } = {}) {
  const ms = typeof iso === 'string' ? Date.parse(iso) : NaN;
  if (!Number.isFinite(ms)) return null;
  const local = clockParts(ms, viewerTz);
  const home = clockParts(ms, PHOENIX);
  if (!local || !home) return null;
  const until = untilWords(ms, now);
  return {
    at: new Date(ms).toISOString(),
    clock: local.full,
    short: local.short,
    line: until ? `Recharges at ${local.full} · in ${until}` : 'Recharging now',
    phoenix: local.full === home.full ? null : `(${home.short} Phoenix)`,
  };
}

/**
 * The support link's words (Nina 09-29: "Help keep the lights on in {name of space}").
 * No name = the plain line; a long name is trimmed so the link stays one tidy line.
 * `short` = the resting banner's shorter form ("Keep the lights on in …").
 */
export function lightsLabel(spaceName, { short = false } = {}) {
  const base = short ? 'Keep the lights on' : 'Help keep the lights on';
  // a featured competition's room is named "{template} - Competition" (the masthead drops it too)
  const name = typeof spaceName === 'string' ? spaceName.replace(/\s+/g, ' ').replace(/\s*-\s*Competition\s*$/i, '').trim() : '';
  if (!name) return base;
  // Count characters, not UTF-16 units: a cut must never split an emoji in half.
  const chars = Array.from(name);
  if (chars.length <= 32) return `${base} in ${name}`;
  let cut = chars.slice(0, 31).join('');
  const space = cut.lastIndexOf(' ');
  if (space >= 20) cut = cut.slice(0, space); // end on a whole word when one ends near the cut
  return `${base} in ${cut.trimEnd()}…`;
}

// 💛 The funding meter (backend AI_POWER_FUNDING_ENABLED; spec §3.4 option C, Nina's pick 09-29):
// "This month, visitors covered N% of the AI bill", and "100% crowd funded" only when it is true.
export const FUNDING_COPY = {
  covered: (pct) => `This month, visitors covered ${pct}% of the AI bill`,
  full: '100% crowd funded this month',
  zero: 'Be the first to chip in this month.',
};

/**
 * The status block's `funding` { month, frac, full, zero } → { frac (0..1, the
 * bar), line, full } | null. "100% crowd funded" ONLY when the backend says the
 * month's tips cover its bill (full); a share short of that never rounds up to
 * 100 (at most 99, the bar at most 0.99) and a real one never reads 0 (at least 1).
 */
export function fundingView(f) {
  if (!f || typeof f !== 'object') return null;
  const raw = typeof f.frac === 'number' && Number.isFinite(f.frac) ? Math.max(0, f.frac) : null;
  if (raw === null) return null;
  if (f.full === true) return { frac: 1, line: FUNDING_COPY.full, full: true };
  if (f.zero === true || raw <= 0) return { frac: 0, line: FUNDING_COPY.zero, full: false };
  const pct = Math.min(99, Math.max(1, Math.round(raw * 100)));
  return { frac: Math.min(0.99, raw), line: FUNDING_COPY.covered(pct), full: false };
}

/**
 * ai_power → { level, asleep, fill, color, text, head, body, foot, aria, support, recharge, funding } | null.
 * `isHouse` picks "The house" vs "The AIs here" (every space since 09-26 pm).
 * `viewerTz` / `now`: the recharge countdown's clock (the viewer's own by default).
 */
export function meterView(p, { tz = null, isHouse = true, viewerTz = null, now = Date.now(), spaceName = null } = {}) {
  if (!p || typeof p !== 'object') return null;
  const asleep = p.asleep === true || p.level === 'asleep';
  const level = asleep ? 'asleep' : (LEVELS.includes(p.level) ? p.level : 'ok');
  const h = (!asleep && typeof p.hours_left === 'number' && Number.isFinite(p.hours_left)) ? p.hours_left : null;
  const runs = isHouse ? 'The house runs' : 'The AIs here run';
  const dozes = 'everyone dozes off until the next top-up';
  const notes = 'Notes you leave wait for them.';
  // 🔋 The recharge window (absent = today). Asleep, only OUR scheduled sleep
  // may show it: a real $0 never gets a countdown.
  const recharge = rechargeView(p.next_recharge_at, { viewerTz, now });
  const windowSleep = asleep && p.asleep_reason === 'window' && !!recharge;
  const showRecharge = asleep ? windowSleep : !!recharge;
  const frac = typeof p.window_left_frac === 'number' && Number.isFinite(p.window_left_frac) ? p.window_left_frac : null;
  // Low, but the recharge lands before the battery empties (the mockup's "Recharge comes first").
  // Only when the WINDOW is what runs low (limited_by 'window'): a recharge moves no money, so an
  // account holding less than the window must never read "Recharging at 5 PM".
  const rechargeFirst = !asleep && !!recharge && p.limited_by === 'window' && (level === 'low' || level === 'critical')
    && h !== null && (Date.parse(recharge.at) - now) / 3600e3 <= h;
  let head;
  let body;
  if (windowSleep) {
    const ran = clockParts(Date.parse(p.since || ''), viewerTz);
    head = `Back at ${recharge.short}`;
    body = `Every space here runs on one shared AI battery. It ran out${ran ? ` at ${ran.full}` : ''}, so everyone dozed off until it recharges. ${notes}`;
  } else if (asleep) {
    const at = clockIn(p.since, tz);
    head = 'Resting until the next top-up';
    body = `${runs} on a prepaid AI balance, and it ran out${at ? ` at ${at}` : ''}, so everyone dozed off. ${notes}`;
  } else if (h === null) {
    head = level === 'critical' ? 'Almost out' : (recharge ? 'The shared AI battery' : (isHouse ? 'The house’s AI balance' : 'The AI balance'));
    body = `${runs} on a prepaid AI balance. If it runs out, ${dozes}. ${notes}`;
  } else if (h >= DAY_HOURS) {
    head = 'More than a day left';
    body = `${runs} on a prepaid AI balance. At today’s pace it lasts more than a day.`;
  } else if (level === 'critical' || h < 2) {
    head = 'Almost out';
    body = `${runs} on a prepaid AI balance. At today’s pace it has ${hoursSpoken(h)} left. Then ${dozes}. ${notes}`;
  } else {
    head = `About ${Math.round(h)} hours left`;
    body = `${runs} on a prepaid AI balance. At today’s pace it lasts ${hoursSpoken(h)} more. If it runs out, ${dozes}. ${notes}`;
  }
  if (!asleep && recharge) body = RECHARGE_BODY; // 🔋 the head keeps the hours; the body says how the battery works
  const url = typeof p.support_url === 'string' && /^https:\/\/\S+$/i.test(p.support_url) ? p.support_url : null;
  const note = typeof p.support_note === 'string' && p.support_note.trim() ? p.support_note.trim() : null;
  const aria = asleep ? 'AI balance: resting until the next top-up'
    : (h === null ? 'AI balance' : (h >= DAY_HOURS ? 'AI balance: more than a day left' : `AI balance: ${hoursSpoken(h)} left`));
  let text = asleep ? '' : hoursWords(h);
  if (windowSleep) text = `Back at ${recharge.short}`;
  else if (rechargeFirst) text = `Recharging at ${recharge.short}`;
  // Nina 09-26: the link only ever shows WITH the note (where the money goes).
  const support = url && note ? { url, note, label: lightsLabel(spaceName) } : null;
  return {
    level,
    asleep,
    fill: !asleep && frac !== null ? Math.max(0.06, Math.min(1, frac)) : meterFill(level, h),
    color: rechargeFirst ? POWER_COLORS.low : POWER_COLORS[level],
    text,
    head,
    body,
    foot: h !== null ? 'Estimated from the last 6 hours. Updated every 10 minutes.' : null,
    aria: windowSleep ? `AI balance: resting, back at ${recharge.short}`
      : (rechargeFirst ? `${aria}, recharging at ${recharge.short}` : aria),
    support,
    recharge: showRecharge ? { at: recharge.at, line: recharge.line, phoenix: recharge.phoenix } : null,
    // 💛 the funding meter only ever sits beside the link (the backend sends it only with one)
    funding: support ? fundingView(p.funding) : null,
  };
}
